import { SelectableOptionMenuItemType } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPSpinner } from 'baseComponents';
import { IPComboBox } from 'baseComponents/Combobox/IPComboBox';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import AuditTrailAction from 'models/auditTrailAction';
import { EntityTypes } from 'models/entity';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAuditActions } from 'services/Api/auditService';
import { apiRequest } from 'services/Auth/authConfig';
import { sortOnCode } from 'utils/sorting';

export type AuditActionPickerGroups = EntityTypes.Risk | EntityTypes.Control;
export const AuditActionPickerGroupCodes: { [key: number]: string } = {
  [EntityTypes.Risk]: 'ATA-R-',
  [EntityTypes.Control]: 'ATA-C-',
};

interface IAuditActionPickerProps {
  label?: string;
  groups: AuditActionPickerGroups[];
  onSelect: (items: number[]) => void;
  selectedAuditActions?: number[];
}

const AuditActionPicker = (props: IAuditActionPickerProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [auditactions, setAuditActions] = useState<AuditTrailAction[]>([]);
  const [selectedAuditActions, setSelectedAuditActions] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  //
  // Effects
  //
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedAuditActions(props.selectedAuditActions ?? []);
  }, [props.selectedAuditActions]);

  //
  // Loading
  //
  const loadData = async () => {
    try {
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      let actions = await apiGetAuditActions(accessToken);
      actions = filterByGroups(actions, props.groups);
      actions.sort((a, b) => sortOnCode(a.name, b.name));

      setAuditActions(actions);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterByGroups = (actions: AuditTrailAction[], groups: AuditActionPickerGroups[]): AuditTrailAction[] => {
    for (const group of groups) {
      const code = AuditActionPickerGroupCodes[group];
      if (code) {
        actions = actions.filter((action) => action.code.startsWith(code));
      }
    }

    return actions;
  };

  //
  // Helpers
  //
  const getOptions = () => {
    return [
      {
        key: -1,
        text: t('widgets:Tenant.Select.SelectAll'),
        itemType: SelectableOptionMenuItemType.SelectAll,
      },
      {
        key: -2,
        text: '-',
        itemType: SelectableOptionMenuItemType.Divider,
      },
      ...auditactions.map((auditaction) => ({
        key: auditaction.auditTrailActionId,
        text: auditaction.name,
        data: auditaction,
      })),
    ];
  };

  //
  // Main render
  //
  if (isLoading) {
    return <IPSpinner size={IPSpinnerSize.large} />;
  }

  return (
    <IPComboBox
      label={props.label}
      options={getOptions()}
      multiSelect
      selectedKey={[selectedAuditActions.length === auditactions.length ? -1 : 0, ...selectedAuditActions]}
      useComboBoxAsMenuWidth
      onChange={(ev, option) => {
        if (option) {
          let selected: number[] = [];
          if (option?.itemType === SelectableOptionMenuItemType.SelectAll) {
            const allSelected = selectedAuditActions.length === auditactions.length;
            selected = allSelected ? [] : auditactions.map((a) => a.auditTrailActionId);
          } else {
            selected = option.selected
              ? [...selectedAuditActions, option.key as number]
              : selectedAuditActions.filter((t) => t !== option.key);
          }
          setSelectedAuditActions(selected);
          props.onSelect(selected);
        }
      }}
      styles={{ optionsContainer: { maxHeight: '40vh' } }}
      text={t('translation:General.Notifications.ItemCount', { count: selectedAuditActions.length })}
    />
  );
};

export default AuditActionPicker;
